import type { PaataleberettigetResolution as PaataleberettigetResolutionData } from "./ANNLLMAnalyzer";
import "./PaataleberettigetResolution.css";

const STATUS_TEXT: Record<PaataleberettigetResolutionData["status"], string> = {
   likely: "Sandsynligt forslag",
   ambiguous: "Flere mulige efterfølgere",
   not_found: "Ingen aktuel påtaleberettiget fundet",
   error: "Onlinesøgningen mislykkedes",
};

function formatConfidence(confidence?: number): string | null {
   if (confidence === undefined || confidence === null || !Number.isFinite(confidence)) {
      return null;
   }
   return `${Math.round(Math.max(0, Math.min(1, confidence)) * 100)} %`;
}

function getSafeSourceUrl(url: string): string | null {
   try {
      const parsed = new URL(url);
      return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.href : null;
   } catch {
      return null;
   }
}

export function PaataleberettigetResolution({
   resolution,
}: {
   resolution: PaataleberettigetResolutionData;
}) {
   const showsSuggestion = Boolean(resolution.suggested_current_name);
   const confidenceLabel = formatConfidence(resolution.confidence);

   return (
      <section
         className={`paatale-resolution paatale-resolution--${resolution.status}`}
         aria-label="AI-foreslået aktuel påtaleberettiget"
      >
         <div className="paatale-resolution__header">
            <strong>AI-foreslået aktuel påtaleberettiget</strong>
            <span className="paatale-resolution__status">{STATUS_TEXT[resolution.status]}</span>
         </div>

         {showsSuggestion ? (
            <div className="paatale-resolution__suggestion">
               <span className="paatale-resolution__name">{resolution.suggested_current_name}</span>
               {confidenceLabel ? (
                  <span className="paatale-resolution__confidence">
                     AI-sikkerhed: {confidenceLabel}
                  </span>
               ) : null}
            </div>
         ) : (
            <p className="paatale-resolution__message">{STATUS_TEXT[resolution.status]}</p>
         )}

         {resolution.reasoning ? <p>{resolution.reasoning}</p> : null}

         {resolution.successor_chain.length > 0 ? (
            <details className="paatale-resolution__details">
               <summary>Se efterfølgerkæde ({resolution.successor_chain.length})</summary>
               <ol className="paatale-resolution__chain">
                  {resolution.successor_chain.map((step, index) => (
                     <li key={`${step.from}-${step.to}-${index}`}>
                        <strong>{step.from} → {step.to}</strong>
                        <div>{step.relationship}</div>
                        {step.effective_date ? <div>Gældende fra: {step.effective_date}</div> : null}
                        {step.evidence ? <p>{step.evidence}</p> : null}
                     </li>
                  ))}
               </ol>
            </details>
         ) : null}

         {resolution.status === "ambiguous" && resolution.alternatives.length > 0 ? (
            <details className="paatale-resolution__details">
               <summary>Se andre mulige påtaleberettigede ({resolution.alternatives.length})</summary>
               <ul className="paatale-resolution__alternatives">
                  {resolution.alternatives.map((alternative, index) => (
                     <li key={`${alternative.name}-${index}`}>
                        <strong>{alternative.name}</strong>
                        {alternative.reasoning ? <p>{alternative.reasoning}</p> : null}
                     </li>
                  ))}
               </ul>
            </details>
         ) : null}

         {resolution.sources.length > 0 ? (
            <div className="paatale-resolution__sources">
               <strong>Kilder</strong>
               <ul>
                  {resolution.sources.map((source, index) => {
                     const safeUrl = getSafeSourceUrl(source.url);
                     return (
                        <li key={`${source.url}-${index}`}>
                           {safeUrl ? (
                              <a href={safeUrl} target="_blank" rel="noopener noreferrer">
                                 {source.title || source.url}
                              </a>
                           ) : (
                              <span>{source.title || source.url}</span>
                           )}
                        </li>
                     );
                  })}
               </ul>
            </div>
         ) : null}

         {resolution.disclaimer ? (
            <p className="paatale-resolution__disclaimer">{resolution.disclaimer}</p>
         ) : null}
      </section>
   );
}
